import map from 'lodash/map'
import range from 'lodash/range'
import find from 'lodash/find'
import startCase from 'lodash/startCase'
import * as React from 'react'
import {H2} from '@frontrow/console/src/components/typography'
import {Button} from '@frontrow/console/src/components/button'
import Input from '@frontrow/console/src/entities/components/base/input'
import {Label} from '@frontrow/console/src/components/ui/label'
import {type StoreCategoryT, getItemUrl} from '@frontrow/console/src/entities/models/item'
import {type StoreCategoryVariationT} from '@frontrow/console/src/entities/models/store-category-variation'

import AvatarWithItem from '@frontrow/console/src/components/pages/piggy-store-page/avatar-with-item'
import FormField from '@frontrow/console/src/components/common/form-field'
import {DEFAULT_PIGGY_STORE_GRADE, MATURE_PIGGY_STORE_GRADE} from './../piggy-store'

const storeCategories: Record<StoreCategoryT, Array<string>> = {
  avatar: ['avatar'],
  clothes: ['legs', 'torso'],
  hats_and_wigs: ['wig'],
  accessories: ['ears', 'face', 'head', 'torso-accessory'],
  scenes: ['background'],
  background: ['background']
}

const avatars = ['piggy', 'fox', 'penguin', 'hippo']

type Props = {
  name: string
  locizeKey: string
  onChangeName: (newName: string) => void
  category: string
  storeCategory: StoreCategoryT
  storeCategoryVariations: Array<StoreCategoryVariationT>
  onChangeCategory: (newCategory: string) => void
  onChangeStoreCategory: (newCategory: StoreCategoryT) => void
  onChangeMinGrade: (minGrade: number) => void
  onChangeMaxGrade: (maxGrade: number) => void
  price: number
  onChangePrice: (newPriceStr: string) => void
  isHidden: boolean
  minGrade: number
  maxGrade: number
  onChangeIsHidden: () => void
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
  disabled: boolean
}

export default function Form(props: Props): React.ReactElement {
  const {
    name,
    locizeKey,
    onChangeName,
    category,
    storeCategory,
    storeCategoryVariations,
    onChangeCategory,
    onChangeStoreCategory,
    onChangeMinGrade,
    onChangeMaxGrade,
    price,
    onChangePrice,
    isHidden,
    minGrade,
    maxGrade,
    onChangeIsHidden,
    onSubmit,
    disabled
  } = props

  const gradeOptions = map(range(13), grade => (
    <option key={grade} value={grade}>
      {grade}
    </option>
  ))

  const categoryOptions = map(storeCategories[storeCategory], subcategory => (
    <option key={subcategory} value={subcategory}>
      {startCase(subcategory)}
    </option>
  ))

  const storeCategoryOptions = map(Object.keys(storeCategories), storeCategory => (
    <option key={storeCategory} value={storeCategory}>
      {storeCategory === 'hats_and_wigs' ? 'Wigs' : startCase(storeCategory)}
    </option>
  ))

  const categoryVariations = find(storeCategoryVariations, x => x.storeCategory === storeCategory)

  const noOp = () => {}

  return (
    <form onSubmit={onSubmit}>
      <H2>Item Information</H2>
      <div className="p-4 bg-white border border-chrome rounded">
        <FormField label="Item Name" required>
          <Input required value={name} type="text" onChange={onChangeName} />
        </FormField>
        <FormField label="Locize Key">
          <Input value={locizeKey} type="text" disabled onChange={noOp} />
        </FormField>

        <FormField label="Item Store Category" required>
          <select
            required
            onChange={event => {
              const newStoreCategory = event.target.value
              onChangeStoreCategory(newStoreCategory as StoreCategoryT)
              onChangeCategory(storeCategories[newStoreCategory as keyof typeof storeCategories][0])
            }}
            value={storeCategory}
          >
            <option key="0" value={''}>
              &mdash;
            </option>
            {storeCategoryOptions}
          </select>
        </FormField>

        <FormField label="Item Subcategory" required>
          <select
            required
            onChange={event => onChangeCategory(event.target.value)}
            value={category}
          >
            <option key="0" value={''}>
              &mdash;
            </option>
            {categoryOptions}
          </select>
        </FormField>

        <FormField label="Price" required>
          <Input value={isNaN(price) ? '' : price} type="number" onChange={onChangePrice} />
        </FormField>
        <FormField label="Min Grade">
          <select
            required
            onChange={event => onChangeMinGrade(parseInt(event.target.value, 10))}
            value={minGrade}
          >
            {gradeOptions}
          </select>
        </FormField>
        <FormField label="Max Grade">
          <select
            required
            onChange={event => onChangeMaxGrade(parseInt(event.target.value, 10))}
            value={maxGrade}
          >
            {gradeOptions}
          </select>
        </FormField>
      </div>

      <H2>Assets</H2>
      <div className="flex flex-wrap">
        {itemOnAvatarNodes(avatars, name, category, categoryVariations)}
      </div>

      <H2>Store Preview</H2>
      <div className="flex flex-wrap">
        {itemInDrawerNodes(avatars, name, category, categoryVariations)}
      </div>

      <Label>
        <Input type="checkbox" checked={isHidden} onChange={onChangeIsHidden} />
        Hide from piggy store
      </Label>

      <Button type="submit" disabled={disabled}>
        Submit
      </Button>
    </form>
  )
}

function simplifyUrl(imgUrl: string): string {
  return imgUrl.split('?').slice(0, 1).join('').replace('https://images.freckle.com', '')
}

function itemOnAvatar(props: {
  name: string
  category: string
  avatar: string
  grade: number
  itemVariationName?: string
}): React.ReactElement {
  const {name, category, avatar, grade, itemVariationName} = props
  const itemUrl = getItemUrl({
    itemName: name,
    itemCategory: category,
    avatarName: avatar,
    avatarPose: 'normal',
    studentGrade: grade,
    trimmedImage: false,
    itemVariationName
  })
  return (
    <div
      className="p-4 bg-white border border-chrome rounded m-1 flex flex-col items-center [&_img]:max-w-[360px]"
      key={`${avatar}-${grade}`}
    >
      <AvatarWithItem avatarName={avatar} itemName={name} itemCategory={category} grade={grade} />
      <small>{simplifyUrl(itemUrl)}</small>
    </div>
  )
}

function itemInDrawer(props: {
  name: string
  category: string
  avatar: string
  grade: number
  itemVariationName?: string
}): React.ReactElement {
  const {name, category, avatar, grade, itemVariationName} = props
  const itemUrl = getItemUrl({
    itemName: name,
    itemCategory: category,
    avatarName: avatar,
    avatarPose: 'normal',
    studentGrade: grade,
    trimmedImage: true,
    itemVariationName
  })
  return (
    <div
      className="p-4 bg-white border border-chrome rounded m-1 flex flex-col items-center [&_img]:max-w-[360px]"
      key={`${avatar}-${grade}`}
    >
      <img title={avatar} alt={avatar} src={itemUrl} />
      <small>{simplifyUrl(itemUrl)}</small>
    </div>
  )
}

function itemOnAvatarNodes(
  avatars: Array<string>,
  name: string,
  category: string,
  categoryVariations?: StoreCategoryVariationT | null
): React.ReactNode {
  const itemOnAvatarYoungerNode = map(avatars, avatar =>
    itemOnAvatar({name, category, avatar, grade: DEFAULT_PIGGY_STORE_GRADE})
  )
  const itemOnAvatarMatureDefaultNode = map(avatars, avatar =>
    itemOnAvatar({name, category, avatar, grade: MATURE_PIGGY_STORE_GRADE})
  )
  const itemOnAvatarMatureNode =
    categoryVariations &&
    categoryVariations.itemVariations &&
    categoryVariations.itemVariations.length
      ? [
          ...itemOnAvatarMatureDefaultNode,
          ...map(categoryVariations.itemVariations, itemVariation =>
            map(avatars, avatar =>
              itemOnAvatar({
                name,
                category,
                avatar,
                grade: MATURE_PIGGY_STORE_GRADE,
                itemVariationName: itemVariation.name
              })
            )
          )
        ]
      : itemOnAvatarMatureDefaultNode
  return [...itemOnAvatarYoungerNode, ...itemOnAvatarMatureNode]
}

function itemInDrawerNodes(
  avatars: Array<string>,
  name: string,
  category: string,
  categoryVariations?: StoreCategoryVariationT | null
): React.ReactNode {
  const itemInDrawerYoungerNode = map(avatars, avatar =>
    itemInDrawer({name, category, avatar, grade: DEFAULT_PIGGY_STORE_GRADE})
  )
  const itemInDrawerMatureDefaultNode = map(avatars, avatar =>
    itemInDrawer({name, category, avatar, grade: MATURE_PIGGY_STORE_GRADE})
  )
  const itemInDrawerMatureNode =
    categoryVariations &&
    categoryVariations.itemVariations &&
    categoryVariations.itemVariations.length
      ? [
          ...itemInDrawerMatureDefaultNode,
          ...map(categoryVariations.itemVariations, variation =>
            map(avatars, avatar =>
              itemInDrawer({
                name,
                category,
                avatar,
                grade: MATURE_PIGGY_STORE_GRADE,
                // ATTENTION: The previous version of this line did not make sense
                itemVariationName: variation.name
              })
            )
          )
        ]
      : itemInDrawerMatureDefaultNode
  return [...itemInDrawerYoungerNode, ...itemInDrawerMatureNode]
}
